import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApiLoadingListener } from '../hooks/useApiLoadingListener';
import { useLoading } from '../contexts/LoadingContext';
import EnvironmentIndicator from './EnvironmentIndicator';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  ShoppingCart as OrdersIcon,
  AddCircle as NewOrderIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Help as HelpIcon,
  AccountBalanceWallet as WalletIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionBanner from './SubscriptionBanner';
import Logo from './Logo';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
  { text: 'Orders', icon: <OrdersIcon />, path: '/orders' },
  { text: 'New Order', icon: <NewOrderIcon />, path: '/new-order' },
  { text: 'Wallet', icon: <WalletIcon />, path: '/wallet' },
  { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  { text: 'Need Help', icon: <HelpIcon />, path: null, isHelp: true },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Initialize API loading listener
  useApiLoadingListener();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { setLoading, setLoadingMessage } = useLoading();

  // Hide loading when route changes
  useEffect(() => {
    setLoading(false);
  }, [location.pathname, setLoading]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    // Show loading for tab switches
    if (location.pathname !== path) {
      setLoadingMessage('Loading page...');
      setLoading(true);
    }
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleHelpClick = () => {
    setContactDialogOpen(true);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
    handleProfileMenuClose();
  };

  const confirmLogout = () => {
    logout();
    setLogoutDialogOpen(false);
  };

  const cancelLogout = () => {
    setLogoutDialogOpen(false);
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ justifyContent: 'center', py: 2 }}>
        <Logo size="medium" showText={true} />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={!item.isHelp && location.pathname === item.path}
              onClick={() => item.isHelp ? handleHelpClick() : handleNavigation(item.path!)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: !item.isHelp && location.pathname === item.path ? 'primary.contrastText' : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'TailorStitchPro'}
          </Typography>
          
          {/* User Profile Menu */}
          <Button
            onClick={handleProfileMenuOpen}
            startIcon={<AccountCircleIcon />}
            sx={{ color: 'text.primary', textTransform: 'none' }}
          >
            {user?.name || 'User'}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px', // AppBar height
        }}
      >
        <SubscriptionBanner />
        {children}
      </Box>

      {/* Contact Support Dialog */}
      <Dialog 
        open={contactDialogOpen} 
        onClose={handleCloseContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Need Help? Contact Support
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 3, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="body1" sx={{ mb: 2, color: 'primary.contrastText' }}>
              If you're experiencing any issues or need assistance, please contact our support team:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                  Ganesh:
                </Typography>
                <Typography variant="body1" sx={{ color: 'primary.contrastText' }}>
                  9019433655
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.contrastText', fontWeight: 'bold' }}>
                  Escalation - Adarsha Sarnad:
                </Typography>
                <Typography variant="body1" sx={{ color: 'primary.contrastText' }}>
                  9880882518
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: 'primary.contrastText', fontStyle: 'italic' }}>
              We're here to help you with any questions or technical issues you may have.
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logout Confirmation Dialog */}
      <Dialog 
        open={logoutDialogOpen} 
        onClose={cancelLogout}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout? You will need to login again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelLogout} variant="outlined">
            Cancel
          </Button>
          <Button onClick={confirmLogout} variant="contained" color="error">
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Environment Indicator */}
      <EnvironmentIndicator />
    </Box>
  );
};

export default Layout; 